import React from 'react';
import { toast } from 'react-toastify';
import Toast from '../components/Toast';

/**
 * Plain-text paste support shared by the Quill editors (stimulus + question).
 *
 * Both editors deliberately refuse pasted formatting: the toolbar is the only
 * formatting path, so a paste is flattened to text before it is inserted. The
 * browser's `text/plain` flavour is used when it has content. When it is blank
 * (Word leaves it empty for some mixed text + picture selections) the text is
 * derived from `text/html` instead, so the words still land in the editor.
 * Images are never inserted; when the paste carried any (files, or `<img>`
 * elements in the HTML) the user gets a toast instead of a silent drop.
 */

export const IMAGE_PASTE_MESSAGE =
  "Images can't be pasted here, only text is supported";

export const STIMULUS_IMAGE_PASTE_MESSAGE =
  "Images can't be pasted here, use the upload tab to add images";

// JED-1171: Quill's insertText freezes the tab on multi-million-character
// pastes, so oversized text is refused before the editor is touched. Same
// limit as the feedback editor (components/QuillEditor).
export const MAX_PASTE_CHARS = 100000;

export const PASTE_TOO_LARGE_MESSAGE =
  'That paste is too large to add here, please paste less text';

const IMAGE_PASTE_TOAST_ID = 'editor-image-paste';

// Elements whose boundaries become line breaks when HTML is flattened, the way
// a browser's innerText treats block-level boxes. Table cells are handled
// separately (tab-separated within a row).
const BLOCK_TAGS = new Set([
  'ADDRESS',
  'ARTICLE',
  'ASIDE',
  'BLOCKQUOTE',
  'DD',
  'DETAILS',
  'DIV',
  'DL',
  'DT',
  'FIELDSET',
  'FIGCAPTION',
  'FIGURE',
  'FOOTER',
  'FORM',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HEADER',
  'HR',
  'LI',
  'MAIN',
  'NAV',
  'OL',
  'P',
  'PRE',
  'SECTION',
  'SUMMARY',
  'TABLE',
  'TBODY',
  'TFOOT',
  'THEAD',
  'TR',
  'UL',
]);

// Elements whose text is never user content (Word pastes ship a <style> block).
const SKIPPED_TAGS = new Set([
  'HEAD',
  'LINK',
  'META',
  'NOSCRIPT',
  'SCRIPT',
  'STYLE',
  'TEMPLATE',
  'TITLE',
]);

const TEXT_NODE = 3;
const ELEMENT_NODE = 1;
const COLLAPSIBLE_WHITESPACE = /[ \t\r\n\f]+/g;
const IMG_TAG = /<img\b/i;

const normaliseLineBreaks = (text) => text.replace(/\r\n?/g, '\n');

const parseHtml = (html) => {
  if (typeof DOMParser === 'undefined') {
    return null;
  }
  try {
    return new DOMParser().parseFromString(html, 'text/html');
  } catch (error) {
    return null;
  }
};

const isCellTag = (tag) => tag === 'TD' || tag === 'TH';

// Trailing whitespace is removed with a backward scan rather than a `/\s+$/`
// regex: on a long whitespace run the regex retries from every position and
// goes quadratic, which froze the editor on a large <pre> paste.
const TRAILING_WHITESPACE = new Set([' ', '\t', '\n', '\r', ' ']);
const trimTrailingWhitespace = (line) => {
  let end = line.length;
  while (end > 0 && TRAILING_WHITESPACE.has(line[end - 1])) {
    end -= 1;
  }
  return end === line.length ? line : line.slice(0, end);
};

const startsNewWord = (lastChar) =>
  lastChar === '' || lastChar === ' ' || lastChar === '\t' || lastChar === '\n';

// Flattens the children of `root` into lines. Text is accumulated per line in
// chunks (never re-scanned) so the walk stays linear in the document size.
const flattenNode = (root, preformatted) => {
  const lines = [];
  let chunks = [];
  let lastChar = '';

  const finishLine = () => {
    lines.push(trimTrailingWhitespace(chunks.join('')));
    chunks = [];
    lastChar = '';
  };
  // A block boundary only ends a line that has content. `<p>&nbsp;</p>` (how
  // Word writes a blank paragraph) counts as content and becomes an empty
  // line, while source-formatting whitespace between blocks does not.
  const endBlock = () => {
    if (chunks.length > 0) {
      finishLine();
    }
  };

  const visit = (node, pre) => {
    if (node.nodeType === TEXT_NODE) {
      let text = node.nodeValue || '';
      if (!pre) {
        text = text.replace(COLLAPSIBLE_WHITESPACE, ' ');
        if (startsNewWord(lastChar)) {
          text = text.replace(/^ /, '');
        }
      }
      if (text) {
        chunks.push(text);
        lastChar = text[text.length - 1];
      }
      return;
    }
    if (node.nodeType !== ELEMENT_NODE) {
      return;
    }
    const tag = node.tagName.toUpperCase();
    if (SKIPPED_TAGS.has(tag)) {
      return;
    }
    if (tag === 'BR') {
      finishLine();
      return;
    }
    if (tag === 'TR') {
      // Each cell is flattened on its own so a row stays one tab-separated
      // line: empty cells keep their tab, and paragraphs inside a cell join
      // with spaces instead of breaking the row.
      endBlock();
      const cells = Array.from(node.children)
        .filter((child) => isCellTag(child.tagName.toUpperCase()))
        .map((cell) => flattenNode(cell, pre).replace(/\n/g, ' '));
      lines.push(cells.join('\t'));
      return;
    }
    const isBlock = BLOCK_TAGS.has(tag) || isCellTag(tag);
    if (isBlock) {
      endBlock();
    }
    const childPreformatted = pre || tag === 'PRE';
    node.childNodes.forEach((child) => visit(child, childPreformatted));
    if (isBlock) {
      endBlock();
    }
  };

  root.childNodes.forEach((child) => visit(child, preformatted));
  endBlock();
  while (lines.length > 0 && lines[lines.length - 1] === '') {
    lines.pop();
  }
  return lines.join('\n');
};

const documentToPlainText = (doc) => {
  if (!doc || !doc.body) {
    return '';
  }
  return flattenNode(doc.body, false);
};

/**
 * Flatten an HTML string to plain text, keeping paragraph / line breaks for
 * block elements and <br> but dropping every other bit of formatting.
 */
export const htmlToPlainText = (html) => {
  if (!html) {
    return '';
  }
  try {
    return documentToPlainText(parseHtml(html));
  } catch (error) {
    // Pathological markup must never break the paste; treat it as no text.
    return '';
  }
};

const getData = (clipboardData, type) => {
  try {
    return clipboardData.getData(type) || '';
  } catch (error) {
    return '';
  }
};

const hasFiles = (clipboardData) => {
  if (clipboardData.files && clipboardData.files.length > 0) {
    return true;
  }
  const items = clipboardData.items ? Array.from(clipboardData.items) : [];
  return items.some((item) => item && item.kind === 'file');
};

/**
 * Work out what a paste should insert.
 *
 * Returns `{ text, hasImages }`: `text` is the plain text to insert (possibly
 * empty) and `hasImages` is true when the clipboard carried files or the HTML
 * flavour contained <img> elements, i.e. something the editor cannot keep.
 * An image-only paste yields no text even when the source app padded the
 * picture with a line break, so dropping the image never leaves a stray line.
 */
export const readClipboardPaste = (clipboardData) => {
  if (!clipboardData) {
    return { text: '', hasImages: false };
  }
  const plainText = normaliseLineBreaks(getData(clipboardData, 'text/plain'));
  const html = getData(clipboardData, 'text/html');
  // A regex test keeps the common text/plain path cheap: the HTML is only
  // parsed when its text is actually needed.
  const hasImages = hasFiles(clipboardData) || IMG_TAG.test(html);

  let text = plainText;
  if (!plainText.trim() && html) {
    text = htmlToPlainText(html) || plainText;
  }
  if (hasImages && !text.trim()) {
    text = '';
  }
  return { text, hasImages };
};

/**
 * Insert plain text at the current Quill selection, replacing any selected
 * range, and leave the caret after the inserted text.
 */
export const insertPlainText = (quill, text) => {
  const selection = quill.getSelection(true);
  const insertIndex = selection ? selection.index : quill.getLength();
  if (selection?.length) {
    quill.deleteText(insertIndex, selection.length, 'user');
  }
  quill.insertText(insertIndex, text, 'user');
  quill.setSelection(insertIndex + text.length, 0, 'silent');
};

/**
 * Build a capture-phase `paste` listener for a Quill instance that enforces the
 * plain-text-only policy. `imagePasteMessage` is the toast shown when images
 * had to be dropped. Nothing touches the editor until the paste is accepted,
 * so a refused paste never deletes the user's current selection.
 */
export const createPlainTextPasteHandler = (
  quill,
  { imagePasteMessage = IMAGE_PASTE_MESSAGE } = {}
) => {
  return (event) => {
    event.preventDefault();
    event.stopPropagation();
    const { text, hasImages } = readClipboardPaste(event.clipboardData);
    if (hasImages) {
      toast(<Toast message={imagePasteMessage} />, {
        toastId: IMAGE_PASTE_TOAST_ID,
      });
    }
    if (!text) {
      return;
    }
    if (text.length > MAX_PASTE_CHARS) {
      toast(<Toast message={PASTE_TOO_LARGE_MESSAGE} />);
      return;
    }
    insertPlainText(quill, text);
  };
};
